import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store/rootState';
import { Dialog, Sort, Filter, SearchInfoTarget, OperationState, TypesFilterFlags } from '../../../store/appState';
import { ResourceStrings } from '../../../ResourceStrings';
import { AuthenticationMessages } from '../Submission/Notifications';
import { ChildrenProps } from '../../../utils/types';
import styled from 'styled-components/macro';
import FeedbackList from './FeedbackList';
import Button from '../../shared/Button';
import * as Actions from '../../../store/actions';
import { SearchResult } from '../../../SearchResults';
import { UserProperties } from '../../../UserProperties';
import { IdeaStates, ProblemStates } from '../../../constants/feedback';
import { Dropdown, IDropdownOption } from '@fluentui/react/lib/Dropdown';
import { Spinner, SpinnerSize } from '@fluentui/react/lib/Spinner';
import RefreshButton from './RefreshButton';
import { MessageBar, MessageBarType } from '@fluentui/react/lib/MessageBar';
import { App, SignInState } from '../../../app';

const feedbackFilterOptions: IDropdownOption[] = [
    { key: TypesFilterFlags.all, text: ResourceStrings.all() },
    { key: TypesFilterFlags.problem, text: ResourceStrings.problems() },
    { key: TypesFilterFlags.idea, text: ResourceStrings.suggestions() },
];

export const isMoreInfoProvided = (item: SearchResult, userProperties: UserProperties): boolean => {
    return ((item != null) &&
        (userProperties.needMoreInfoFromMe[item.trackingId] === false));
}

export const isNeedMoreInfoVisible = (item: SearchResult, userProperties: UserProperties): boolean => {
    return item != null &&
        (
            item.state === ProblemStates.moreInfo
            // @TODO: We might need the commented code again after bug 556370 is fixed in backend
            // || userProperties.needMoreInfoFromMe[item.trackingId] === true
        ) &&
        !isMoreInfoProvided(item, userProperties);
}

const loadNMIItems = (signInState: SignInState): void => {
    if (signInState !== SignInState.Success) {  // My feedback & activities require authenticated user
        return;
    }

    const options: Actions.SearchOptions = new Actions.SearchOptions();
    options.sort = Sort.active;
    options.filter = Filter.follow;
    options.updateCountsByType = true;
    Actions.searchFor("", SearchInfoTarget.myNMIItems, options, "MyFeedbackNMIList");
}

const loadFeedback = (signInState: SignInState, typeFilter: TypesFilterFlags, skip: number = 0): void => {
    if (signInState !== SignInState.Success) {  // My feedback & activities require authenticated user
        return;
    }

    const options: Actions.SearchOptions = new Actions.SearchOptions();
    options.sort = Sort.active;
    options.filter = Filter.follow;
    options.updateCountsByType = true;
    options.type = typeFilter;
    options.skip = skip;
    Actions.searchFor("", SearchInfoTarget.myProblems, options, "MyFeedbackList");
}

export interface MyFeedbackViewProps {
}

export default function MyFeedbackView(props: MyFeedbackViewProps): JSX.Element {
    return <MyFeedbackViewWrapper>
        <MyFeedbackComponent />
    </MyFeedbackViewWrapper>
}

export function MyFeedbackComponent(): JSX.Element {
    const state = useSelector((state: RootState) => state.appState);

    useEffect(() => {
        Actions.setDialog(Dialog.none);
    }, []);

    useEffect(() => {
        loadNMIItems(state.signInState);
    }, [state.settings, state.signInState, state.userVsid]);

    useEffect(() => {
        loadFeedback(state.signInState, state.typeFilter);
    }, [state.typeFilter, state.settings, state.userVsid]);

    const onFilterChange = (event: React.FormEvent<HTMLDivElement>, item: IDropdownOption): void => {
        Actions.setTypeFilter(item.key as TypesFilterFlags);
    };

    const refreshClicked = (typeFilter: TypesFilterFlags): void => {
        loadNMIItems(state.signInState);
        loadFeedback(state.signInState, typeFilter);
    };

    const loadMoreFeedbackClicked = (typeFilter: TypesFilterFlags, skip: number): void => {
        loadFeedback(state.signInState, typeFilter, skip);
    };

    const signinState: number = state.signInState;

    const feedbackList = state.myProblems.results.results;
    let totalFeedbackMatches = 0;
    switch (state.typeFilter) {
        case TypesFilterFlags.problem:
            totalFeedbackMatches = state.myProblems.results.countsByType?.problem ?? 0;
            break;
        case TypesFilterFlags.idea:
            totalFeedbackMatches = state.myProblems.results.countsByType?.idea ?? 0;
            break;
        case TypesFilterFlags.all:
            totalFeedbackMatches = (state.myProblems.results.countsByType?.problem + state.myProblems.results.countsByType?.idea) ?? 0;
    }

    const nmiItemsFilter = (items: SearchResult[]): SearchResult[] => items.filter(x => isNeedMoreInfoVisible(x, state.userProperties));
    const fixedItemsFilter = (items: SearchResult[]): SearchResult[] => items
        .filter(result => {
            return result != null && (
                result.state === ProblemStates.fixed ||
                result.state === IdeaStates.fixed
            );
        })
        .sort((a, b) => {
            if (!a.lastModifiedUtc) {
                return 1;  // b first
            }
            if (!b.lastModifiedUtc) {
                return -1;   // a first
            }
            const dateA = App.parseDate(a.lastModifiedUtc as any);
            const dateB = App.parseDate(b.lastModifiedUtc as any);
            return dateB.getTime() - dateA.getTime();
        })
        .slice(0, 3);

    const fetchState = state.myProblems.state;

    return <Wrapper data-cy="rap-submit-post-view">
        {<AuthenticationMessages
            signinState={signinState}
            signInErrorMessage={state.signInErrorMessage}
            render={(props: ChildrenProps) => <PageContent>{props.children}</PageContent>} />
        }

        {signinState === SignInState.Success && <>
                {state.submittedPostRecently && (
                    <div className="top-level-text">
                        <MessageBar
                            messageBarType={MessageBarType.info}
                            onDismiss={() => { Actions.clearSubmittedPostRecently() }}
                            dismissButtonAriaLabel={ResourceStrings.close()}
                        >
                            {ResourceStrings.recentlySubmittedFeedback()}
                        </MessageBar>
                    </div>
                )}

                <h2 className="top-level-title">My feedback</h2>

                {state.myNMIItems.state === OperationState.Success && <>
                    <h3 className="top-level-text">Needs attention</h3>
                    <FeedbackList feedbackList={state.myNMIItems} itemsFilter={nmiItemsFilter} />
                </>}

                {!!(fetchState === OperationState.Success || (fetchState === OperationState.Executing && state.myProblems.isLoadingMore)) && (<>
                    <h3 className="top-level-text">My recent fixes</h3>
                    <div>
                        <FeedbackList feedbackList={state.myProblems} itemsFilter={fixedItemsFilter} />
                    </div>
                </>)}

                <h3 className="top-level-text">Followed feedback</h3>
                <div className="toolbar top-level-text">
                    <Dropdown
                        options={feedbackFilterOptions}
                        selectedKey={state.typeFilter}
                        ariaLabel={ResourceStrings.postTypeFilter()}
                        onChange={onFilterChange} />

                    <RefreshButton
                        disabled={fetchState === OperationState.Executing}
                        onClick={() => refreshClicked(state.typeFilter)} />
                </div>

                {!!(fetchState === OperationState.Success || (fetchState === OperationState.Executing && state.myProblems.isLoadingMore)) && (
                    <div>
                        <FeedbackList feedbackList={state.myProblems} />
                    </div>
                )}

                {fetchState === OperationState.Success && feedbackList.length < totalFeedbackMatches && (
                    <LoadMore>
                        <Button
                            data-cy="rap-load-more-btn"
                            buttonType="link"
                            onClick={() => loadMoreFeedbackClicked(state.typeFilter, feedbackList.length)}>
                            {ResourceStrings.loadMore()}
                        </Button>
                    </LoadMore>
                )}

                {fetchState === OperationState.Executing && (
                    <Progress>
                        {ResourceStrings.loadingMyItems()}
                        <Spinner size={SpinnerSize.large} />
                    </Progress>
                )}

                {fetchState === OperationState.Failed && <>
                    <div>
                        Error loading your items.
                    </div>
                    <pre>
                        {state.myProblems.errorMessage}
                    </pre>
                </>}
        </>}
    </Wrapper>
}

const MyFeedbackViewWrapper = styled.div`
    padding: 3.5rem var(--page-margin-horizontal);
`;

const Wrapper = styled.div`
    background-color: var(--color-background);

    .top-level-title {
        font-size: 4.2rem;
        line-height: 1.24;
        color: var(--color-on-surface);
        margin: 0 0 1.5rem 0;
        font-weight: normal;
    }
    
    min-height: 60vh;
    margin-left: auto;
    margin-right: auto;

    >* + * {
        margin-top: 4rem;
    }

    >.toolbar + * {
        margin-top: 1.5rem;
    }

    >.toolbar {
        margin-top: 0.5rem;
    }

    >h2 + div {
        margin-top: 1rem;
    }

    >h3 + div {
        margin-top: 1rem;
    }

    .ms-Dropdown-container {
        display: inline-block;
        min-width: 15rem;
        margin-top: .5rem;
    }

    .ms-Dropdown-title {
            border-color: var(--color-surface-dark1);
    }

    .title-instruction {
        font-size: 1.8rem;
        line-height: 2.4rem;
        font-weight: 350;

        .btn-link {
            padding: 0;
            height: auto;
        }
    }

    .selected-post-header.collapsed {
        background-color: var(--color-secondary);
    }

    .selected-post-header {
        font-size: 2.4rem;
    }

    .toolbar {
        display: flex;
        justify-content: space-between;
        align-items: center;
    }

    .ms-MessageBar {
        padding: .2rem 0;

        span {
            font-size: 1.6rem;
        }

        .ms-MessageBar-icon {
            font-size: 1.8rem;
        }
    }
`;

const LoadMore = styled.div`
    text-align: center;
    margin: 0.8rem 0;

    button {
        font-size: 1.4rem;
    }
`;

const PageContent = styled.main`
    min-height: 60vh;
    margin-left: auto;
    margin-right: auto;

    >* + * {
        margin-top: 4rem;
    }

    >.toolbar + * {
        margin-top: 1.5rem;
    }

    >.toolbar {
        margin-top: 0.5rem;
    }

    >h2 + div {
        margin-top: 1rem;
    }

    >h3 + div {
        margin-top: 1rem;
    }

    .ms-Dropdown-container {
        display: inline-block;
        min-width: 15rem;
        margin-top: .5rem;
    }

    .ms-Dropdown-title {
            border-color: var(--color-surface-dark1);
    }

    .title-instruction {
        font-size: 1.8rem;
        line-height: 2.4rem;
        font-weight: 350;

        .btn-link {
            padding: 0;
            height: auto;
        }
    }

    .selected-post-header.collapsed {
        background-color: var(--color-secondary);
    }

    .selected-post-header {
        font-size: 2.4rem;
    }

    .toolbar {
        display: flex;
        justify-content: space-between;
        align-items: center;
    }

    .ms-MessageBar {
        padding: .2rem 0;

        span {
            font-size: 1.6rem;
        }

        .ms-MessageBar-icon {
            font-size: 1.8rem;
        }
    }
`;

const Progress = styled.div`
    text-align: center;
    margin-top: 10rem;

    .ms-Spinner {
        margin-top: 2rem;
    }
`;
